import { useEffect, useState } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { useCart } from '@/hooks/useCart'
import { navigateTo } from '@/lib/navigate'
import FlipButton from '@/components/ui/FlipButton'
import type {
  ArtworkArtist,
  ArtworkDetailPageData,
  ArtworkUpload,
} from '@/types/artwork'

/*
  Figma: node 256:1450 (Artwork Details)
  See in-file comment in the previous revision for layout details.
  Styling lives in src/styles/pages/artwork-detail.css.
*/

/* ---------- Helpers ---------- */

function formatPrice(price: number | null | undefined): string {
  if (price == null || Number.isNaN(price)) return ''
  const rounded = Math.round(price)
  return `$${rounded.toLocaleString()}`
}

function hasContent(html?: string | null): boolean {
  if (!html) return false
  return html.replace(/<[^>]*>/g, '').trim().length > 0
}

function isVideo(upload?: ArtworkUpload | null): boolean {
  if (!upload) return false
  const ext = (upload.extension || '').toLowerCase()
  if (!ext && upload.url) {
    const urlExt = upload.url.split('.').pop()?.toLowerCase() || ''
    return ['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(urlExt)
  }
  return ['mp4', 'webm', 'mov', 'm4v', 'ogv'].includes(ext)
}

function pickImageUrl(up?: ArtworkUpload | null): string | null {
  if (!up) return null
  return up.versions?.large || up.versions?.card || up.url || null
}

/* ---------- SOLD Watermark ---------- */
function SoldWatermark() {
  return (
    <div aria-hidden="true" className="mig-artwork-detail-sold-watermark">
      <div className="mig-artwork-detail-sold-watermark-text">SOLD</div>
    </div>
  )
}

/* ---------- Image Gallery with thumbnails ---------- */
function ImageGallery({
  mainImage,
  extraImages,
  alt,
  showSoldOverlay,
}: {
  mainImage?: ArtworkUpload | null
  extraImages: ArtworkUpload[]
  alt: string
  showSoldOverlay: boolean
}) {
  const allImages: ArtworkUpload[] = []
  if (mainImage) allImages.push(mainImage)
  for (const img of extraImages) {
    if (img && img.url && img.url !== mainImage?.url) allImages.push(img)
  }

  const [activeIdx, setActiveIdx] = useState(0)
  const current = allImages[activeIdx] ?? null
  const currentUrl = pickImageUrl(current)
  const currentIsVideo = isVideo(current)
  const thumbSlots = allImages.length

  return (
    <div className="mig-artwork-detail-gallery">
      <div className="mig-artwork-detail-main-image">
        {currentUrl ? (
          currentIsVideo ? (
            <video src={currentUrl} controls playsInline />
          ) : (
            <img src={currentUrl} alt={alt} fetchPriority="high" decoding="async" />
          )
        ) : (
          <span className="mig-artwork-detail-placeholder">Artwork</span>
        )}
        {showSoldOverlay && <SoldWatermark />}
      </div>

      {thumbSlots > 1 && (
        <div className="mig-artwork-detail-thumbs">
          {Array.from({ length: thumbSlots }).map((_, i) => {
            const thumbImg = allImages[i]
            const thumbUrl = thumbImg ? pickImageUrl(thumbImg) : null
            const isActive = i === activeIdx
            const clickable = !!thumbImg
            return (
              <div
                key={i}
                className="mig-artwork-detail-thumb"
                data-disabled={!clickable}
              >
                <button
                  onClick={() => clickable && setActiveIdx(i)}
                  aria-label={`Show image ${i + 1}`}
                  disabled={!clickable}
                  className="mig-artwork-detail-thumb-button"
                >
                  {thumbUrl && !isVideo(thumbImg) ? (
                    <img src={thumbUrl} alt="" loading="lazy" decoding="async" />
                  ) : null}
                </button>
                <div
                  className="mig-artwork-detail-thumb-underline"
                  data-active={isActive}
                  data-disabled={!clickable}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ---------- CTA Button ----------
   Always renders <button>. Variant (primary/secondary) is driven by
   `data-primary`. */
function Button({
  label,
  primary,
  onClick,
  href,
  disabled = false,
}: {
  label: string
  primary: boolean
  onClick?: () => void
  href?: string
  disabled?: boolean
}) {
  const handleClick = href ? navigateTo(href) : onClick
  return (
    <FlipButton
      onClick={handleClick}
      disabled={disabled}
      variant={primary ? 'filled' : 'outlined'}
      className="mig-artwork-detail-btn"
    >
      {label}
    </FlipButton>
  )
}

/* ---------- Artist section ---------- */
function ArtistSection({ artist }: { artist: ArtworkArtist }) {
  const studioUrl = pickImageUrl(artist.studio_shot_or_video)
  const studioIsVideo = isVideo(artist.studio_shot_or_video)
  const fallbackUrl = pickImageUrl(artist.main_photo)
  const mediaUrl = studioUrl ?? fallbackUrl

  return (
    <div className="mig-artwork-detail-artist-section">
      <div className="mig-artwork-detail-artist-bio">
        {hasContent(artist.bio) ? (
          <div dangerouslySetInnerHTML={{ __html: artist.bio ?? '' }} />
        ) : (
          <p>{`Discover more about ${artist.name}.`}</p>
        )}
      </div>

      <div className="mig-artwork-detail-artist-media">
        {mediaUrl ? (
          studioIsVideo ? (
            <video src={mediaUrl} controls playsInline />
          ) : (
            <img src={mediaUrl} alt={`${artist.name} studio`} loading="lazy" decoding="async" />
          )
        ) : (
          <span className="mig-artwork-detail-artist-placeholder">Studio shot or Video</span>
        )}
      </div>
    </div>
  )
}

/* ---------- Back URL helper ---------- */
function useBackUrl(): string {
  const [backUrl, setBackUrl] = useState('/marketplace')
  useEffect(() => {
    try {
      const ref = document.referrer
      if (!ref) return
      const url = new URL(ref)
      if (url.origin !== window.location.origin) return
      if (url.pathname.startsWith('/artists/')) {
        setBackUrl(url.pathname)
        return
      }
      if (url.pathname === '/marketplace') {
        setBackUrl('/marketplace')
        return
      }
    } catch {
      // ignore invalid referrer
    }
  }, [])
  return backUrl
}

/* ========================= Main Page ========================= */
export default function ArtworkDetail() {
  const data = usePageData<ArtworkDetailPageData>()
  const artwork = data?.artwork ?? null
  const artist = data?.artist ?? null
  const backUrl = useBackUrl()
  const { addItem, items: cartItems } = useCart()

  const [visible, setVisible] = useState(false)
  const [purchaseState, setPurchaseState] = useState<
    { status: 'idle' | 'pending' | 'error'; message?: string }
  >({ status: 'idle' })

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  async function handlePurchase() {
    if (!artwork?.uuid) {
      setPurchaseState({ status: 'error', message: 'This artwork cannot be purchased online yet.' })
      return
    }
    if (cartItems.some((it) => it.product_uuid === artwork.uuid)) {
      window.location.href = '/cart'
      return
    }
    setPurchaseState({ status: 'pending' })
    try {
      await addItem({ product_uuid: artwork.uuid, quantity: 1 })
      window.location.href = '/cart'
    } catch (e) {
      setPurchaseState({
        status: 'error',
        message: e instanceof Error ? e.message : 'Could not add this artwork to the cart.',
      })
    }
  }

  useEffect(() => {
    if (artwork?.name) {
      document.title = `${artwork.name} | Migration Galleries`
    }
  }, [artwork?.name])

  if (!artwork) {
    return (
      <section className="mig-artwork-detail-not-found">
        <h1 className="mig-artwork-detail-page-title">Artwork not found</h1>
      </section>
    )
  }

  const isSold = (artwork.product_status || '').toLowerCase() === 'sold'
  const isInCart = !!artwork.uuid && cartItems.some((it) => it.product_uuid === artwork.uuid)
  const heading = artwork.artist_name ?? artist?.name ?? 'Artist'

  return (
    <section className="mig-artwork-detail-section" data-visible={visible}>
      <div className="mig-artwork-detail-page-title-row">
        <h1 className="mig-artwork-detail-page-title">{heading}</h1>
      </div>

      <div className="mig-artwork-detail-main">
        <ImageGallery
          mainImage={artwork.main_image}
          extraImages={artwork.images ?? []}
          alt={artwork.name}
          showSoldOverlay={isSold}
        />

        <div className="mig-artwork-detail-info">
          <p className="mig-artwork-detail-name">{artwork.name}</p>

          <div className="mig-artwork-detail-description">
            {hasContent(artwork.short_description) && (
              <div
                className="mig-artwork-detail-short-desc"
                dangerouslySetInnerHTML={{ __html: artwork.short_description ?? '' }}
              />
            )}
            {hasContent(artwork.description) && (
              <div
                className="mig-artwork-detail-long-desc"
                dangerouslySetInnerHTML={{ __html: artwork.description ?? '' }}
              />
            )}
            {(hasContent(artwork.year) || hasContent(artwork.materials) || hasContent(artwork.dimensions)) && (
              <div className="mig-artwork-detail-specs">
                {hasContent(artwork.year) && (
                  <p><strong>Year</strong>: {artwork.year}</p>
                )}
                {hasContent(artwork.materials) && (
                  <p><strong>Materials</strong>: {artwork.materials}</p>
                )}
                {hasContent(artwork.dimensions) && (
                  <p><strong>Dimensions</strong>: {artwork.dimensions}</p>
                )}
              </div>
            )}
          </div>

          {artwork.price != null && (
            <p className="mig-artwork-detail-price">{formatPrice(artwork.price)}</p>
          )}

          {!isSold && (
            <div className="mig-artwork-detail-ctas">
              <div className="mig-artwork-detail-cta-row">
                <Button
                  label={
                    isInCart
                      ? 'Already in Cart'
                      : purchaseState.status === 'pending'
                      ? 'Adding…'
                      : 'Purchase'
                  }
                  primary
                  onClick={handlePurchase}
                  disabled={isInCart || purchaseState.status === 'pending'}
                />
                {/* Or Lease button temporarily hidden — uncomment to re-enable once leasing flow is ready to launch. */}
              </div>
              {purchaseState.status === 'error' && (
                <p role="alert" className="mig-artwork-detail-purchase-error">
                  {purchaseState.message}
                </p>
              )}
            </div>
          )}

          {isSold && (
            <p className="mig-artwork-detail-sold-note">This artwork has been sold.</p>
          )}
        </div>
      </div>

      <div className="mig-artwork-detail-bottom">
        <div className="mig-artwork-detail-bottom-inner">
          <div className="mig-divider-thick-12" />

          {artist ? (
            <>
              <ArtistSection artist={artist} />
              <div className="mig-artwork-detail-find-out-more-row">
                <Button label="Find out more" primary href={`/artists/${artist.slug}`} />
              </div>
            </>
          ) : (
            <p className="mig-artwork-detail-artist-unavailable">Artist details unavailable.</p>
          )}
        </div>

        <div className="mig-page-back-row">
          <a href={backUrl} className="mig-artwork-detail-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </div>
    </section>
  )
}
